(() => {
  const init = () => {
    document.querySelectorAll('[data-el-product-v3-hero]').forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = 'true';

      const main = root.querySelector('[data-product-main]');
      const thumbsRoot = root.querySelector('[data-product-thumbs]');
      const thumbs = Array.from(root.querySelectorAll('[data-product-thumb]'));
      const previousButton = root.querySelector('[data-thumb-prev]');
      const nextButton = root.querySelector('[data-thumb-next]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const galleryStatus = root.querySelector('[data-gallery-status]');
      const mediaCache = new Map();
      let selectionVersion = 0;
      let idlePreload = null;
      let destroyed = false;

      const sourceFor = (button) => ({
        src: button?.dataset.src || '',
        srcset: button?.dataset.srcset || '',
        sizes: button?.dataset.sizes || '(min-width: 901px) 50vw, 100vw'
      });
      const sourceKey = (source) => JSON.stringify([
        source.src, source.srcset, source.sizes,
        source.srcset ? window.innerWidth : 0,
        source.srcset ? window.devicePixelRatio : 0
      ]);
      const applySource = (image, source) => {
        image.removeAttribute('srcset');
        image.sizes = source.sizes;
        if (source.srcset) image.srcset = source.srcset;
        image.src = source.src;
      };
      const setGalleryState = (state) => {
        if (!main) return;
        main.dataset.galleryState = state;
        main.parentElement?.setAttribute('aria-busy', String(state === 'loading'));
        if (galleryStatus) {
          galleryStatus.textContent = state === 'loading' ? galleryStatus.dataset.loadingText || '' :
            state === 'error' ? galleryStatus.dataset.errorText || '' : '';
        }
      };
      const loadImage = (source, priority = 'low') => {
        const key = sourceKey(source);
        const cached = mediaCache.get(key);
        if (cached) {
          if (priority === 'high') cached.image.fetchPriority = 'high';
          return cached;
        }
        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = priority;
        const entry = { image, ready: false, promise: null };
        entry.promise = new Promise((resolve, reject) => {
          image.onload = async () => {
            if (typeof image.decode === 'function') {
              try { await image.decode(); } catch (_) {}
            }
            image.onload = null;
            image.onerror = null;
            entry.ready = true;
            resolve(image);
          };
          image.onerror = () => {
            image.onload = null;
            image.onerror = null;
            mediaCache.delete(key);
            reject(new Error('Gallery image could not load'));
          };
        });
        mediaCache.set(key, entry);
        applySource(image, source);
        return entry;
      };
      const canPreload = () => {
        const connection = window.navigator?.connection;
        return !destroyed && !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');
      };
      const warmImage = (button) => {
        if (!main || !button || !canPreload()) return;
        const source = sourceFor(button);
        if (source.src) loadImage(source).promise.catch(() => {});
      };
      const cancelPreload = () => {
        if (idlePreload === null) return;
        if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idlePreload);
        else window.clearTimeout(idlePreload);
        idlePreload = null;
      };
      const warmNeighbors = (button) => {
        cancelPreload();
        if (!canPreload()) return;
        const index = thumbs.indexOf(button);
        const run = () => {
          idlePreload = null;
          warmImage(thumbs[index - 1]);
          warmImage(thumbs[index + 1]);
        };
        idlePreload = typeof window.requestIdleCallback === 'function' ?
          window.requestIdleCallback(run, { timeout: 1500 }) : window.setTimeout(run, 250);
      };

      const activeIndex = () => Math.max(0, thumbs.findIndex((item) => item.classList.contains('is-active')));
      const updateNavigation = () => {
        if (!thumbsRoot || !previousButton || !nextButton) return;
        previousButton.hidden = thumbs.length < 2;
        nextButton.hidden = thumbs.length < 2;
        const index = activeIndex();
        previousButton.disabled = index <= 0;
        nextButton.disabled = index >= thumbs.length - 1;
      };

      const centerThumb = (button) => {
        if (!thumbsRoot || !button) return;
        window.requestAnimationFrame(() => {
          const railBox = thumbsRoot.getBoundingClientRect();
          const buttonBox = button.getBoundingClientRect();
          const buttonCenterInScroll = thumbsRoot.scrollLeft + (buttonBox.left - railBox.left) + (buttonBox.width / 2);
          const target = buttonCenterInScroll - (thumbsRoot.clientWidth / 2);
          const maximum = Math.max(0, thumbsRoot.scrollWidth - thumbsRoot.clientWidth);
          thumbsRoot.scrollTo({
            left: Math.min(maximum, Math.max(0, target)),
            behavior: reducedMotion.matches ? 'auto' : 'smooth'
          });
        });
      };

      const selectThumb = (button, moveFocus = false) => {
        if (!button || destroyed) return;
        thumbs.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
          if (active) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
        });
        const source = sourceFor(button);
        if (main && source.src) {
          const version = ++selectionVersion;
          cancelPreload();
          main.alt = button.dataset.alt || '';
          main.loading = 'eager';
          main.decoding = 'async';
          const entry = loadImage(source, 'high');
          if (entry.ready) {
            applySource(main, source);
            setGalleryState('ready');
            warmNeighbors(button);
          } else {
            const thumbnail = button.querySelector('img');
            const preview = button.dataset.preview ||
              (thumbnail?.complete && thumbnail.naturalWidth > 0 ? thumbnail.currentSrc || thumbnail.src : '');
            applySource(main, preview ? { src: preview, srcset: '', sizes: source.sizes } : source);
            setGalleryState('loading');
            entry.promise.then(() => {
              if (destroyed || version !== selectionVersion) return;
              applySource(main, source);
              setGalleryState('ready');
              warmNeighbors(button);
            }).catch(() => {
              if (destroyed || version !== selectionVersion) return;
              setGalleryState('error');
            });
          }
        }
        if (moveFocus) button.focus({ preventScroll: true });
        centerThumb(button);
        updateNavigation();
      };

      thumbs.forEach((button, index) => {
        button.addEventListener('pointerenter', () => warmImage(button));
        button.addEventListener('focus', () => warmImage(button));
        button.addEventListener('click', () => selectThumb(button));
        button.addEventListener('keydown', (event) => {
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = Math.min(thumbs.length - 1, index + 1);
          else if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = thumbs.length - 1;
          else return;
          event.preventDefault();
          selectThumb(thumbs[nextIndex], true);
        });
      });

      previousButton?.addEventListener('click', () => selectThumb(thumbs[Math.max(0, activeIndex() - 1)], true));
      nextButton?.addEventListener('click', () => selectThumb(thumbs[Math.min(thumbs.length - 1, activeIndex() + 1)], true));
      const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(updateNavigation) : null;
      if (thumbsRoot) resizeObserver?.observe(thumbsRoot);
      if (thumbsRoot) thumbsRoot.scrollLeft = 0;
      updateNavigation();

      if (main) {
        const warmInitialNeighbors = () => {
          if (selectionVersion === 0) warmNeighbors(thumbs[activeIndex()]);
        };
        if (main.complete && main.naturalWidth > 0) warmInitialNeighbors();
        else main.addEventListener('load', warmInitialNeighbors, { once: true });
      }

      const zoomButton = root.querySelector('[data-product-zoom]');
      const viewer = root.querySelector('[data-product-viewer]');
      const viewerImage = viewer?.querySelector('[data-product-viewer-image]');
      const viewerViewport = viewer?.querySelector('[data-product-viewer-viewport]');
      const viewerScale = viewer?.querySelector('[data-product-viewer-scale]');
      const viewerStatus = viewer?.querySelector('[data-product-viewer-status]');
      const viewerClose = viewer?.querySelector('[data-product-viewer-close]');
      const viewerPrev = viewer?.querySelector('[data-product-viewer-prev]');
      const viewerNext = viewer?.querySelector('[data-product-viewer-next]');
      const viewerPosition = viewer?.querySelector('[data-product-viewer-position]');
      let returnFocus = null;
      let savedOverflow = null;
      let lastTouch = null;
      let touchStart = null;
      const resetViewerScale = () => {
        viewerScale?.classList.remove('is-detail');
        viewerScale?.setAttribute('aria-pressed', 'false');
        viewerScale?.setAttribute('aria-label', viewerScale.dataset.zoomLabel || '');
        if (viewerViewport) {
          viewerViewport.scrollLeft = 0;
          viewerViewport.scrollTop = 0;
        }
      };
      const viewerSource = () => {
        const source = sourceFor(thumbs[activeIndex()]);
        const candidates = source.srcset.split(',').map((entry) => {
          const parts = entry.trim().split(/\s+/);
          return { src: parts[0], width: Number.parseInt(parts[1], 10) || 0 };
        }).filter((entry) => entry.src).sort((a, b) => a.width - b.width);
        return candidates.at(-1)?.src || source.src || main?.currentSrc || main?.src || '';
      };
      const renderViewer = () => {
        if (!viewerImage) return;
        resetViewerScale();
        viewerImage.alt = thumbs[activeIndex()]?.dataset.alt || main?.alt || '';
        viewerImage.hidden = false;
        viewerStatus.hidden = false;
        viewerStatus.textContent = viewerStatus.dataset.loading || '';
        viewerViewport.setAttribute('aria-busy', 'true');
        viewerImage.src = viewerSource();
        viewerPrev.disabled = activeIndex() === 0;
        viewerNext.disabled = activeIndex() >= thumbs.length - 1;
        viewerPrev.hidden = thumbs.length < 2;
        viewerNext.hidden = thumbs.length < 2;
        viewerPosition.textContent = (viewer.dataset.positionTemplate || '').replace('__CURRENT__', activeIndex() + 1).replace('__TOTAL__', Math.max(thumbs.length, 1));
      };
      const restoreViewer = () => {
        resetViewerScale();
        if (savedOverflow) {
          document.documentElement.style.overflow = savedOverflow.html;
          document.body.style.overflow = savedOverflow.body;
          savedOverflow = null;
        }
        if (!destroyed) (returnFocus?.isConnected ? returnFocus : zoomButton)?.focus({ preventScroll: true });
        returnFocus = null;
      };
      const openViewer = () => {
        if (!main || !viewer || !viewerImage || viewer.open || destroyed) return;
        if (typeof viewer.showModal !== 'function') {
          window.open(viewerSource(), '_blank', 'noopener');
          return;
        }
        returnFocus = root.contains(document.activeElement) ? document.activeElement : zoomButton;
        savedOverflow = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        renderViewer();
        viewer.showModal();
        viewerClose.focus({ preventScroll: true });
      };
      const moveViewer = (direction) => {
        const index = Math.min(thumbs.length - 1, Math.max(0, activeIndex() + direction));
        selectThumb(thumbs[index]);
        renderViewer();
      };
      if (main && zoomButton && viewer && viewerImage) {
        zoomButton.hidden = false;
        zoomButton.addEventListener('click', openViewer);
        main.addEventListener('dblclick', (event) => { event.preventDefault(); openViewer(); });
        main.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' && event.isPrimary) touchStart = { x: event.clientX, y: event.clientY, time: event.timeStamp };
          else { touchStart = null; lastTouch = null; }
        });
        main.addEventListener('pointercancel', () => { touchStart = null; lastTouch = null; });
        main.addEventListener('pointerup', (event) => {
          if (event.pointerType !== 'touch' || !event.isPrimary || !touchStart) return;
          const start = touchStart;
          touchStart = null;
          if (event.timeStamp - start.time > 350 || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 16) { lastTouch = null; return; }
          if (lastTouch && event.timeStamp - lastTouch.time < 350 && Math.hypot(event.clientX - lastTouch.x, event.clientY - lastTouch.y) < 24) {
            lastTouch = null;
            event.preventDefault();
            openViewer();
          } else lastTouch = { x: event.clientX, y: event.clientY, time: event.timeStamp };
        });
        viewerImage.addEventListener('load', () => { viewerStatus.hidden = true; viewerViewport.setAttribute('aria-busy', 'false'); });
        viewerImage.addEventListener('error', () => {
          viewerImage.hidden = true;
          viewerStatus.hidden = false;
          viewerStatus.textContent = viewerStatus.dataset.error || '';
          viewerViewport.setAttribute('aria-busy', 'false');
        });
        viewerClose.addEventListener('click', () => viewer.close());
        viewer.addEventListener('close', restoreViewer);
        viewer.addEventListener('click', (event) => { if (event.target === viewer) viewer.close(); });
        viewer.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') { event.preventDefault(); moveViewer(-1); }
          if (event.key === 'ArrowRight') { event.preventDefault(); moveViewer(1); }
        });
        viewerPrev.addEventListener('click', () => moveViewer(-1));
        viewerNext.addEventListener('click', () => moveViewer(1));
        viewerScale.addEventListener('click', () => {
          const detail = viewerScale.classList.toggle('is-detail');
          viewerScale.setAttribute('aria-pressed', String(detail));
          viewerScale.setAttribute('aria-label', detail ? viewerScale.dataset.fitLabel : viewerScale.dataset.zoomLabel);
          viewerViewport.scrollLeft = detail ? (viewerViewport.scrollWidth - viewerViewport.clientWidth) / 2 : 0;
          viewerViewport.scrollTop = detail ? (viewerViewport.scrollHeight - viewerViewport.clientHeight) / 2 : 0;
        });
      }

      const sectionEl = root.closest('.shopify-section');
      if (sectionEl) {
        sectionEl.addEventListener('shopify:section:unload', () => {
          destroyed = true;
          if (viewer?.open) viewer.close();
          restoreViewer();
          selectionVersion += 1;
          cancelPreload();
          resizeObserver?.disconnect();
          mediaCache.clear();
          delete root.dataset.ready;
        }, { once: true });
      }

      const qty = root.querySelector('[name="quantity"]');
      const unit = Number(root.dataset.price || 0);
      const total = root.querySelector('[data-product-total]');
      const updateQuantity = () => {
        const count = Math.min(20, Math.max(1, Number(qty?.value || 1)));
        if (qty) qty.value = String(count);
        if (total) total.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency', currency: root.dataset.currency || 'USD'
        }).format((window.ELCampingOffer?.applies(root.dataset.productHandle, root.querySelector('[name="id"]')?.value) ? window.ELCampingOffer.total(unit, count) : unit * count) / 100);
      };
      root.querySelector('[data-qty-minus]')?.addEventListener('click', () => { if (qty) qty.value = String(Number(qty.value) - 1); updateQuantity(); });
      root.querySelector('[data-qty-plus]')?.addEventListener('click', () => { if (qty) qty.value = String(Number(qty.value) + 1); updateQuantity(); });
      qty?.addEventListener('input', updateQuantity);
      document.addEventListener('easylonger:camping-change', updateQuantity);
      sectionEl?.addEventListener('shopify:section:unload', () => document.removeEventListener('easylonger:camping-change', updateQuantity), { once: true });
      updateQuantity();

      root.querySelectorAll('.el-product-v8__details').forEach((details) => {
        const summary = details.querySelector('summary');
        if (!summary) return;
        const syncExpanded = () => summary.setAttribute('aria-expanded', String(details.open));
        details.addEventListener('toggle', syncExpanded);
        syncExpanded();
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
